import { type KeyboardEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MicButton } from '../../components/speech-to-text';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { DictationLiveStatus } from './DictationLiveStatus';
import { autoGrow, MAX_LENGTH } from './internal-notes-utils';
import { useNoteDictation } from './useNoteDictation';
import { Group, Stack } from '@folio/components/ui/flex';

interface NoteEditorProps {
  noteId: string;
  value: string;
  onValueChange: (next: string) => void;
  onSave: () => void | Promise<void>;
  onCancel: () => void;
  speechToTextEnabled: boolean;
}

export function NoteEditor({
  noteId,
  value,
  onValueChange,
  onSave,
  onCancel,
  speechToTextEnabled,
}: NoteEditorProps) {
  const { t } = useTranslation();
  const { textareaRef, dictation, recording, display } = useNoteDictation({
    value,
    onValueChange,
  });

  // Focus + place caret at end on mount. Deferred one frame because the
  // dropdown's close auto-focus would otherwise win the race and steal focus
  // back to the trigger button.
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      autoGrow(el);
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
    });
    return () => cancelAnimationFrame(id);
  }, [textareaRef]);

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void onSave();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
    }
  }

  return (
    <Stack gap={1} className="bg-muted/40 border-border rounded-md border px-2 py-1.5">
      <Group align="start" gap={2}>
        <textarea
          ref={textareaRef}
          value={display}
          onChange={event => onValueChange(event.target.value)}
          onKeyDown={onKeyDown}
          readOnly={recording}
          maxLength={MAX_LENGTH}
          data-testid={`internal-note-edit-${noteId}`}
          rows={1}
          className={cn(
            'w-full resize-none whitespace-pre-wrap bg-transparent text-sm outline-none',
            dictation.partial && 'text-muted-foreground italic',
          )}
        />
        {speechToTextEnabled && (
          <MicButton state={dictation.state} onStart={dictation.start} onStop={dictation.stop} />
        )}
      </Group>
      <DictationLiveStatus recording={recording} dictation={dictation} />
      <Group align="stretch" justify="end" gap={2}>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onCancel}
          data-testid={`internal-note-edit-cancel-${noteId}`}
        >
          {t('internalNotes.edit.cancel')}
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => void onSave()}
          disabled={!value.trim() || recording}
          data-testid={`internal-note-edit-save-${noteId}`}
        >
          {t('internalNotes.edit.save')}
        </Button>
      </Group>
    </Stack>
  );
}
